// ============================================================================
// XTRACKER INSTRUMENT EDITOR — Dual ADSR envelope canvas editor
// ============================================================================
// Two separate canvases: volume (y=0..64) and panning (y=0..32, center=32)
// Each has its own sustain/loop controls.

let currentInstrument = null;

// Per-canvas state
const envState = {
   volume: {
      canvas: null, ctx: null,
      dragPoint: null, isDragging: false,
      resizeObserver: null,
   },
   panning: {
      canvas: null, ctx: null,
      dragPoint: null, isDragging: false,
      resizeObserver: null,
   },
};

// Layout constants
const PAD_L = 30, PAD_R = 10, PAD_T = 10, PAD_B = 20;
const POINT_RADIUS = 5;
const HIT_RADIUS = 8;

export function initInstrumentEditor() {
   // Volume canvas
   const volCanvas = document.getElementById('xt-ie-vol-canvas');
   if (volCanvas) {
      envState.volume.canvas = volCanvas;
      envState.volume.ctx = volCanvas.getContext('2d');
      wireCanvas('volume');
      if (window.ResizeObserver) {
         envState.volume.resizeObserver = new ResizeObserver(() => draw('volume'));
         envState.volume.resizeObserver.observe(volCanvas);
      }
   }
   // Panning canvas
   const panCanvas = document.getElementById('xt-ie-pan-canvas');
   if (panCanvas) {
      envState.panning.canvas = panCanvas;
      envState.panning.ctx = panCanvas.getContext('2d');
      wireCanvas('panning');
      if (window.ResizeObserver) {
         envState.panning.resizeObserver = new ResizeObserver(() => draw('panning'));
         envState.panning.resizeObserver.observe(panCanvas);
      }
   }
   wireControls();
   wireEnvTabs();
}

export function loadInstrument(instrument) {
   currentInstrument = instrument;
   draw('volume');
   draw('panning');
   syncUI();
}

function getEnvelope(type) {
   if (!currentInstrument) return null;
   return type === 'volume' ? currentInstrument.volumeEnvelope : currentInstrument.panningEnvelope;
}

function getMaxY(type) {
   return 64; // both volume and panning use 0-64 in XM format
}

function getCenterY(type) {
   return type === 'volume' ? 0 : 32; // pan center is at y=32
}

// ---- Coordinate conversion ----
function toCanvas(type, x, y, w, h) {
   const env = getEnvelope(type);
   if (!env) return [0, 0];
   const maxX = Math.max(1, ...env.points.map(p => p[0]));
   const maxY = getMaxY(type);
   const cx = PAD_L + (x / maxX) * (w - PAD_L - PAD_R);
   const cy = PAD_T + (1 - (y / maxY)) * (h - PAD_T - PAD_B);
   return [cx, cy];
}

function fromCanvas(type, cx, cy, w, h) {
   const env = getEnvelope(type);
   if (!env) return [0, 0];
   const maxX = Math.max(1, ...env.points.map(p => p[0]));
   const maxY = getMaxY(type);
   const x = ((cx - PAD_L) / (w - PAD_L - PAD_R)) * maxX;
   const y = (1 - (cy - PAD_T) / (h - PAD_T - PAD_B)) * maxY;
   return [Math.round(x), Math.round(Math.max(0, Math.min(maxY, y)))];
}

// ---- Drawing ----
function resizeCanvas(type) {
   const s = envState[type];
   if (!s.canvas || !s.ctx) return [0, 0];
   const dpr = window.devicePixelRatio || 1;
   const w = s.canvas.clientWidth;
   const h = s.canvas.clientHeight;
   if (w < 10 || h < 10) return [0, 0];
   const targetW = Math.round(w * dpr);
   const targetH = Math.round(h * dpr);
   if (s.canvas.width !== targetW || s.canvas.height !== targetH) {
      s.canvas.width = targetW;
      s.canvas.height = targetH;
   }
   s.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
   return [w, h];
}

export function draw(type) {
   const s = envState[type];
   if (!s.canvas || !s.ctx) return;
   const [w, h] = resizeCanvas(type);
   if (w < 10 || h < 10) return;
   const ctx = s.ctx;
   const tc = window.themeColors || [];
   const env = getEnvelope(type);
   const maxY = getMaxY(type);

   // Background
   ctx.fillStyle = tc[0] || '#0a0a0a';
   ctx.fillRect(0, 0, w, h);

   // Grid — horizontal lines
   ctx.strokeStyle = tc[3] || '#222';
   ctx.lineWidth = 0.5;
   for (let i = 0; i <= 4; i++) {
      const y = PAD_T + (i / 4) * (h - PAD_T - PAD_B);
      ctx.beginPath();
      ctx.moveTo(PAD_L, y);
      ctx.lineTo(w - PAD_R, y);
      ctx.stroke();
   }
   // Grid — vertical lines
   for (let i = 0; i <= 4; i++) {
      const x = PAD_L + (i / 4) * (w - PAD_L - PAD_R);
      ctx.beginPath();
      ctx.moveTo(x, PAD_T);
      ctx.lineTo(x, h - PAD_B);
      ctx.stroke();
   }

   // Axis labels
   ctx.fillStyle = tc[6] || '#555';
   ctx.font = '9px monospace';
   ctx.textAlign = 'right';
   ctx.fillText(String(maxY), PAD_L - 4, PAD_T + 8);
   ctx.fillText('0', PAD_L - 4, h - PAD_B);
   ctx.textAlign = 'center';
   ctx.fillText(type.toUpperCase() + ' ENV', w / 2, h - 4);

   if (!env || !env.points || env.points.length === 0) return;

   // Center line for pan (y = 32 = middle, 0 = full left, 64 = full right)
   if (type === 'panning') {
      const [, cy] = toCanvas(type, 0, 32, w, h);
      ctx.strokeStyle = tc[5] || '#444';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(PAD_L, cy);
      ctx.lineTo(w - PAD_R, cy);
      ctx.stroke();
      ctx.setLineDash([]);
      // Labels: top = R (y=64), middle = C (y=32), bottom = L (y=0)
      ctx.fillStyle = tc[6] || '#555';
      ctx.font = '8px monospace';
      ctx.textAlign = 'left';
      ctx.fillText('R', PAD_L + 2, PAD_T + 8);         // top = full right
      ctx.fillText('C', PAD_L + 2, cy - 2);             // middle = center
      ctx.fillText('L', PAD_L + 2, h - PAD_B - 2);      // bottom = full left
   }

   // Sustain line
   if (env.sustain && env.sustainPoint < env.points.length) {
      const sp = env.points[env.sustainPoint];
      const [sx] = toCanvas(type, sp[0], sp[1], w, h);
      ctx.strokeStyle = tc[11] || '#888';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(sx, PAD_T);
      ctx.lineTo(sx, h - PAD_B);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = tc[11] || '#888';
      ctx.font = '8px monospace';
      ctx.textAlign = 'left';
      ctx.fillText('S', sx + 2, PAD_T + 8);
   }

   // Loop region
   if (env.loop && env.loopStartPoint < env.loopEndPoint) {
      const lp = env.points[env.loopStartPoint];
      const rp = env.points[env.loopEndPoint];
      const [lx] = toCanvas(type, lp[0], lp[1], w, h);
      const [rx] = toCanvas(type, rp[0], rp[1], w, h);
      ctx.fillStyle = (tc[9] || '#69b') + '22';
      ctx.fillRect(lx, PAD_T, Math.max(2, rx - lx), h - PAD_T - PAD_B);
   }

   // Envelope line
   const enabled = env.enabled;
   ctx.strokeStyle = enabled ? (tc[14] || '#ccc') : (tc[6] || '#555');
   ctx.lineWidth = 1.5;
   ctx.beginPath();
   for (let i = 0; i < env.count; i++) {
      const p = env.points[i];
      if (!p) continue;
      const [px, py] = toCanvas(type, p[0], p[1], w, h);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
   }
   ctx.stroke();

   // Points
   for (let i = 0; i < env.count; i++) {
      const p = env.points[i];
      if (!p) continue;
      const [px, py] = toCanvas(type, p[0], p[1], w, h);
      ctx.fillStyle = (i === s.dragPoint) ? (tc[15] || '#fff') : (enabled ? (tc[14] || '#ccc') : (tc[6] || '#555'));
      ctx.beginPath();
      ctx.arc(px, py, POINT_RADIUS, 0, 2 * Math.PI);
      ctx.fill();
      // Highlight sustain point
      if (env.sustain && i === env.sustainPoint) {
         ctx.strokeStyle = tc[11] || '#888';
         ctx.lineWidth = 1.5;
         ctx.beginPath();
         ctx.arc(px, py, POINT_RADIUS + 2, 0, 2 * Math.PI);
         ctx.stroke();
      }
   }
}

// ---- Canvas interaction: drag points, add/delete ----
function wireCanvas(type) {
   const s = envState[type];
   const canvas = s.canvas;

   canvas.addEventListener('mousedown', (e) => {
      if (!currentInstrument) return;
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const w = rect.width, h = rect.height;
      if (w < 10 || h < 10) return;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const env = getEnvelope(type);
      if (!env) return;
      // Find closest point
      let bestIdx = -1, bestDist = HIT_RADIUS;
      for (let i = 0; i < env.count; i++) {
         const p = env.points[i];
         if (!p) continue;
         const [px, py] = toCanvas(type, p[0], p[1], w, h);
         const d = Math.hypot(mx - px, my - py);
         if (d < bestDist) { bestDist = d; bestIdx = i; }
      }
      if (bestIdx >= 0) {
         if (e.button === 2) {
            e.preventDefault();
            if (env.count > 2) {
               env.points.splice(bestIdx, 1);
               env.count = env.points.length;
               if (env.sustainPoint >= bestIdx) env.sustainPoint = Math.max(0, env.sustainPoint - 1);
               if (env.loopStartPoint >= bestIdx) env.loopStartPoint = Math.max(0, env.loopStartPoint - 1);
               if (env.loopEndPoint >= bestIdx) env.loopEndPoint = Math.max(0, env.loopEndPoint - 1);
               draw(type);
               syncUI();
            }
            return;
         }
         s.dragPoint = bestIdx;
         s.isDragging = true;
      } else if (e.button === 0) {
         const [x, y] = fromCanvas(type, mx, my, w, h);
         let insertIdx = env.count;
         for (let i = 0; i < env.count; i++) {
            if (env.points[i] && x < env.points[i][0]) { insertIdx = i; break; }
         }
         env.points.splice(insertIdx, 0, [x, y]);
         env.count = env.points.length;
         s.dragPoint = insertIdx;
         s.isDragging = true;
         draw(type);
         syncUI();
      }
   });

   canvas.addEventListener('mousemove', (e) => {
      if (!s.isDragging || s.dragPoint == null) return;
      const rect = canvas.getBoundingClientRect();
      const w = rect.width, h = rect.height;
      if (w < 10 || h < 10) return;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const env = getEnvelope(type);
      if (!env) return;
      let [x, y] = fromCanvas(type, mx, my, w, h);
      if (s.dragPoint > 0 && env.points[s.dragPoint - 1]) x = Math.max(x, env.points[s.dragPoint - 1][0] + 1);
      if (s.dragPoint < env.count - 1 && env.points[s.dragPoint + 1]) x = Math.min(x, env.points[s.dragPoint + 1][0] - 1);
      if (s.dragPoint === 0) x = 0;
      env.points[s.dragPoint] = [x, y];
      draw(type);
   });

   document.addEventListener('mouseup', () => {
      if (s.isDragging) {
         s.isDragging = false;
         s.dragPoint = null;
         draw(type);
      }
   });

   canvas.addEventListener('contextmenu', (e) => e.preventDefault());
}

// ---- Sub-tabs: VOL / PAN ----
function wireEnvTabs() {
   const tabs = document.querySelectorAll('.xt-ie-env-tab');
   tabs.forEach(tab => {
      tab.addEventListener('click', () => {
         const target = tab.dataset.envtab;
         tabs.forEach(t => t.classList.toggle('active', t === tab));
         // Toggle control groups in menubar
         $('xt-ie-vol-ctrls').hidden = (target !== 'vol');
         $('xt-ie-pan-ctrls').hidden = (target !== 'pan');
         // Toggle canvases in content area
         $('xt-ie-vol-canvas').hidden = (target !== 'vol');
         $('xt-ie-pan-canvas').hidden = (target !== 'pan');
         // Redraw after visibility change
         requestAnimationFrame(() => {
            draw('volume');
            draw('panning');
         });
      });
   });
}

// ---- Controls: enable, sustain, loop (per envelope) ----
function wireControls() {
   const bind = (id, fn) => {
      const el = document.getElementById(id);
      if (el) el.addEventListener('change', fn);
   };

   // Volume envelope
   bind('xt-ie-volenv', () => {
      const env = currentInstrument && currentInstrument.volumeEnvelope;
      if (env) env.enabled = document.getElementById('xt-ie-volenv').checked;
      draw('volume');
   });
   bind('xt-ie-vol-sustain', () => {
      const env = getEnvelope('volume');
      if (env) env.sustain = document.getElementById('xt-ie-vol-sustain').checked;
      draw('volume');
   });
   const vsp = document.getElementById('xt-ie-vol-sustain-point');
   if (vsp) vsp.addEventListener('change', () => {
      const env = getEnvelope('volume');
      if (env) env.sustainPoint = Math.max(0, Math.min(env.count - 1, parseInt(vsp.value, 10) || 0));
      draw('volume');
   });
   bind('xt-ie-vol-loop', () => {
      const env = getEnvelope('volume');
      if (env) env.loop = document.getElementById('xt-ie-vol-loop').checked;
      draw('volume');
   });
   const vls = document.getElementById('xt-ie-vol-loop-start');
   const vle = document.getElementById('xt-ie-vol-loop-end');
   if (vls) vls.addEventListener('change', () => {
      const env = getEnvelope('volume');
      if (env) env.loopStartPoint = Math.max(0, Math.min(env.count - 1, parseInt(vls.value, 10) || 0));
      draw('volume');
   });
   if (vle) vle.addEventListener('change', () => {
      const env = getEnvelope('volume');
      if (env) env.loopEndPoint = Math.max(0, Math.min(env.count - 1, parseInt(vle.value, 10) || 0));
      draw('volume');
   });

   // Panning envelope
   bind('xt-ie-panenv', () => {
      const env = currentInstrument && currentInstrument.panningEnvelope;
      if (env) env.enabled = document.getElementById('xt-ie-panenv').checked;
      draw('panning');
   });
   bind('xt-ie-pan-sustain', () => {
      const env = getEnvelope('panning');
      if (env) env.sustain = document.getElementById('xt-ie-pan-sustain').checked;
      draw('panning');
   });
   const psp = document.getElementById('xt-ie-pan-sustain-point');
   if (psp) psp.addEventListener('change', () => {
      const env = getEnvelope('panning');
      if (env) env.sustainPoint = Math.max(0, Math.min(env.count - 1, parseInt(psp.value, 10) || 0));
      draw('panning');
   });
   bind('xt-ie-pan-loop', () => {
      const env = getEnvelope('panning');
      if (env) env.loop = document.getElementById('xt-ie-pan-loop').checked;
      draw('panning');
   });
   const pls = document.getElementById('xt-ie-pan-loop-start');
   const ple = document.getElementById('xt-ie-pan-loop-end');
   if (pls) pls.addEventListener('change', () => {
      const env = getEnvelope('panning');
      if (env) env.loopStartPoint = Math.max(0, Math.min(env.count - 1, parseInt(pls.value, 10) || 0));
      draw('panning');
   });
   if (ple) ple.addEventListener('change', () => {
      const env = getEnvelope('panning');
      if (env) env.loopEndPoint = Math.max(0, Math.min(env.count - 1, parseInt(ple.value, 10) || 0));
      draw('panning');
   });
}

export function syncUI() {
   if (!currentInstrument) return;
   const ve = currentInstrument.volumeEnvelope;
   const pe = currentInstrument.panningEnvelope;
   const set = (id, val) => { const el = document.getElementById(id); if (el) el.value = val; };
   const setChk = (id, val) => { const el = document.getElementById(id); if (el) el.checked = val; };
   // Volume
   setChk('xt-ie-volenv', !!(ve && ve.enabled));
   if (ve) {
      setChk('xt-ie-vol-sustain', !!ve.sustain);
      set('xt-ie-vol-sustain-point', ve.sustainPoint || 0);
      setChk('xt-ie-vol-loop', !!ve.loop);
      set('xt-ie-vol-loop-start', ve.loopStartPoint || 0);
      set('xt-ie-vol-loop-end', ve.loopEndPoint || 0);
   }
   // Panning
   setChk('xt-ie-panenv', !!(pe && pe.enabled));
   if (pe) {
      setChk('xt-ie-pan-sustain', !!pe.sustain);
      set('xt-ie-pan-sustain-point', pe.sustainPoint || 0);
      setChk('xt-ie-pan-loop', !!pe.loop);
      set('xt-ie-pan-loop-start', pe.loopStartPoint || 0);
      set('xt-ie-pan-loop-end', pe.loopEndPoint || 0);
   }
}

// Helper for $ since we don't import it
function $(id) { return document.getElementById(id); }
